<template>
  <div
    class="d-comments-group"
    :class="{ 'multiple': hasMultipleEntries,
              'replying': isReplying }"
  >
    <div class="comment-entries">
      <DCommentEntry
        :comment="comment"
        :is-user-signed-in="!!userName"
        :item-slug="itemSlug"
        :item-type="itemType"
        @comment-removed="removeCommentsGroup"
        @reply="reply"
      />

      <div
        v-if="displayedReplies.length"
        class="replies"
      >
        <DCommentEntry
          v-for="commentReply in displayedReplies"
          :key="commentReply.id"
          :comment="commentReply"
          :is-user-signed-in="!!userName"
          :item-slug="itemSlug"
          :item-type="itemType"
          @comment-removed="removeInnerComment"
          @reply="reply"
        />
      </div>

      <div
        v-if="canExpand"
        class="load-more-replies comment-item grid-flex grid-middle grid-no-response"
      >
        <div class="grid-item-shrink avatar">
          <a
            title="Load more replies"
            @click="expand"
          >+{{ moreNumber }}</a>
        </div>

        <div class="grid-item">
          <a
            title="Load more replies"
            @click="expand"
          >Load more replies</a>
        </div>
      </div>

      <div
        v-if="newReplies.length"
        class="replies"
      >
        <DCommentEntry
          v-for="commentReply in newReplies"
          :key="commentReply.id"
          :comment="commentReply"
          :is-user-signed-in="!!userName"
          :item-slug="itemSlug"
          :item-type="itemType"
          @comment-removed="removeInnerComment"
          @reply="reply"
        />
      </div>
    </div>

    <DCommentBox
      v-if="(isUserSignedIn && isReplying)"
      ref="commentBox"
      :auto-focus="true"
      :item-slug="itemSlug"
      :item-type="itemType"
      :mentions="replyMentions"
      :parent-id="comment.id"
      placeholder="Write a reply..."
      :user-avatar-url="userAvatarUrl"
      :user-name="userName"
      :value="replyText"
      @comment-added="replyAdded"
    />
  </div>
</template>

<script>
import DCommentBox from '@d-components/d-comments-panel/d-comment-box/d-comment-box.vue';
import DCommentEntry from './d-comment-entry/d-comment-entry.vue';
import MentionsHelper from '@d-helpers/mentions-helper';

export default {
  components: {
    DCommentBox,
    DCommentEntry,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    isUserSignedIn: {
      type: Boolean,
      default: false,
    },
    itemSlug: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      validator: function (value) {
        return [
          'book',
          'post',
          'user-review',
        ].indexOf(value) !== -1;
      },
      required: true,
    },
    numDisplayedReplies: {
      type: Number,
      default: 1,
    },
    userAvatarUrl: {
      type: String,
      default: null,
    },
    userName: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    isExpanded: false,
    isReplying: false,
    replyMentions: null,
    replyText: null,
    newReplies: [],
  }),
  computed: {
    canExpand() {
      if (this.isExpanded ||
        !this.comment.children ||
        this.comment.children.length <= this.numDisplayedReplies) {
        return false;
      }

      return true;
    },
    displayedReplies() {
      if (!this.comment.children) {
        return [];
      }

      if (!this.canExpand) {
        return this.comment.children;
      }

      return this.comment.children.slice(0, this.numDisplayedReplies);
    },
    hasMultipleEntries() {
      return this.isReplying || (this.comment.children && !!this.comment.children.length);
    },
    moreNumber() {
      if (!this.comment.children || !this.comment.children.length) {
        return null;
      }

      return this.comment.children.length - this.numDisplayedReplies;
    },
  },
  methods: {
    expand() {
      this.$set(this, 'isExpanded', true);
    },

    async reply(comment) {
      if (this.isReplying) {
        this.$refs.commentBox.focus();
        return;
      }

      const replyText = MentionsHelper.getMentionPlaceholder(comment.user_id);
      this.$set(this, 'replyMentions', [
        {
          id: comment.user_id,
          label: comment.user_username,
          url: comment.user_url,
        },
      ]);
      this.$set(this, 'replyText', replyText);
      this.$set(this, 'isReplying', true);
      await this.$nextTick();
      this.$refs.commentBox.focus();
    },

    removeCommentsGroup() {
      this.$emit('remove-comment-group', this.comment);
    },

    removeInnerComment(removedComment) {
      let commentIdx = this.comment.children ? this.comment.children.findIndex(c => c.id === removedComment.id) : -1;

      if (commentIdx >= 0) {
        this.$emit('inner-comment-removed', {
          childId: commentIdx,
          parentId: this.comment.id,
        });
        return;
      }

      commentIdx = this.newReplies.findIndex(c => c.id === removedComment.id);
      if (commentIdx >= 0) {
        this.newReplies.splice(commentIdx, 1);
        this.$emit('inner-comment-removed');
      }
    },

    replyAdded(comment) {
      this.newReplies.push(comment);
      this.$set(this, 'isReplying', false);
      this.$set(this, 'replyMentions', null);
      this.$set(this, 'replyText', null);
    },
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

.d-comments-group {
  background-color: getColor($white);
  border-radius: $border-radius-base;
  margin: 0 (-$_2em) (-$_2em) (-$_2em);
  padding: $_2em;

  @include transition((background-color));

  .comment-entries .replies .d-comment-entry,
  .d-comment-box {
    margin-top: $_1em;
  }

  .comment-entries .replies {
    .avatar img {
      width: $_3em;
      height: $_3em;
      margin-top: $_1em;
      margin-left: $_0-5em;
    }
  }

  // Connecting line
  .comment-entries,
  .d-comment-box {
    position: relative;
  }

  &.multiple .comment-entries:before,
  .d-comment-box:before {
    background-color: getColor($grey, medium);
    content: " ";
    display: inline-block;
    left: ($_2em + ($border-width-base * 0.5));
    position: absolute;
    width: $border-width-base;
    z-index: 0;
  }

  &.multiple .comment-entries:before {
    height: calc(100% - #{$_6em});
    top: ($_4em + $border-width-base);
  }

  .d-comment-box {
    &:before {
      height: $_8em;
      top: -$_5em;
    }
  }
  // END Connecting line

  .avatar {
    height: ($_4em + (2 * $border-width-base));
    width: ($_4em + (2 * $border-width-base));

    img,
    a {
      position: absolute;
      z-index: 1;
    }
  }

  .load-more-replies.comment-item {
    font-size: $font-size-sm;
    margin-top: $_0-5em;

    a {
      color: getColor($grey, dark);

      @include transition((color));

      &:hover {
        color: getColor($black);
      }
    }

    .avatar {
      position: relative;

      a {
        background-color: getColor($grey, medium);
        border: $border-width-base solid getColor($white);
        border-radius: ($_2em + $border-width-base);
        height: $_4em;
        line-height: $_4em;
        min-width: $_3em;
        padding: 0 $_0-5em;
        right: 0;
        text-align: center;

        @include transition((border-color, color));

        &:hover {
          border-color: getColor($black);
        }
      }
    }
  }
}
</style>
